import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BASE_URL_PROVIDER } from '@imagise/shared';
import { map, Observable, throwError } from 'rxjs';
import { ErrorReportRequest, ErrorReportResponse } from './error-reporting.contracts';
import { MAX_REPORT_FILES, MAX_REPORT_FILE_BYTES, MAX_REPORT_TOTAL_FILE_BYTES } from './error-report-operation';

@Injectable({ providedIn: 'root' })
export class ErrorReportingFacade {
  readonly #http = inject(HttpClient);
  readonly #baseUrl = inject(BASE_URL_PROVIDER);

  reportError(request: ErrorReportRequest, accessToken?: string): Observable<ErrorReportResponse> {
    if (request.Kind === 'feedback' && !accessToken) {
      return throwError(() => new HttpErrorResponse({ status: 401, statusText: 'Authentication required' }));
    }
    const files = request.Files ?? [];
    if (files.length > MAX_REPORT_FILES || files.some(file => file.blob.size > MAX_REPORT_FILE_BYTES) ||
        files.reduce((size, file) => size + file.blob.size, 0) > MAX_REPORT_TOTAL_FILE_BYTES) {
      return throwError(() => new HttpErrorResponse({ status: 413, statusText: 'Report files exceed limits' }));
    }
    const form = new FormData();
    for (const field of ['ReportId', 'Kind', 'App', 'Title', 'Message', 'Detail', 'OriginalUrl', 'Email', 'IsPrivatePost'] as const) {
      const value = request[field];
      if (value !== undefined && value !== null) form.append(field, String(value));
    }
    for (const file of files) form.append('Files', file.blob, file.name.replace(/^.*[\\/]/, ''));
    const route = request.Kind === 'feedback' ? 'ReportFeedback' : 'ReportError';
    const headers = accessToken
      ? new HttpHeaders({ Authorization: `Bearer ${accessToken}` }) : undefined;
    return this.#http.post<unknown>(`${this.#baseUrl.replace(/\/+$/, '')}/${route}`, form, { headers }).pipe(
      map(response => this.#normalize(response, request))
    );
  }

  #normalize(response: unknown, request: ErrorReportRequest): ErrorReportResponse {
    const record = response && typeof response === 'object' && !Array.isArray(response)
      ? response as Record<string, unknown> : {};
    const read = (pascal: string, camel: string): unknown => Object.hasOwn(record, pascal) ? record[pascal] : record[camel];
    const attachmentsPrivate = read('AttachmentsPrivate', 'attachmentsPrivate');
    const acknowledged = read('ReportId', 'reportId') === request.ReportId &&
      read('AcceptedFileCount', 'acceptedFileCount') === request.Files.length && attachmentsPrivate === request.IsPrivatePost;
    const url = read('Url', 'url');
    return {
      ReportId: typeof read('ReportId', 'reportId') === 'string' ? read('ReportId', 'reportId') as string : undefined,
      AcceptedFileCount: acknowledged ? request.Files.length : undefined,
      AttachmentsPrivate: typeof attachmentsPrivate === 'boolean' ? attachmentsPrivate : undefined,
      IsSuccess: acknowledged && read('IsSuccess', 'isSuccess') === true,
      Queued: acknowledged && read('Queued', 'queued') === true,
      TopicUrlAccessible: acknowledged && read('TopicUrlAccessible', 'topicUrlAccessible') === true,
      Error: null,
      Url: acknowledged && typeof url === 'string' ? url : null,
    };
  }
}
